import { useMemo, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { FiHelpCircle } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'
import { resolveGuide } from './guideResolver'
import SmartGuidePanel from './SmartGuidePanel'

interface SmartGuideButtonProps {
  inline?: boolean
  className?: string
}

/**
 * Compact floating help button + cinematic walkthrough for the current route.
 */
export default function SmartGuideButton({ inline = false, className = '' }: SmartGuideButtonProps) {
  const location = useLocation()
  const { language } = useLanguageStore()
  const isAr = language === 'ar'
  const [open, setOpen] = useState(false)
  const [pulse, setPulse] = useState(true)

  const guide = useMemo(() => resolveGuide(location.pathname), [location.pathname])

  useEffect(() => {
    setOpen(false)
    const seenKey = `smartGuideSeen_${location.pathname}`
    setPulse(!localStorage.getItem(seenKey))
  }, [location.pathname])

  const openGuide = () => {
    setOpen(true)
    setPulse(false)
    localStorage.setItem(`smartGuideSeen_${location.pathname}`, '1')
  }

  const label = isAr ? 'شرح الشاشة' : language === 'fr' ? 'Guide' : 'Page guide'

  if (inline) {
    return (
      <>
        <button
          type="button"
          onClick={openGuide}
          className={`guide-inline-btn relative inline-flex h-7 w-7 items-center justify-center rounded-full border border-teal-400/40 bg-teal-500/10 text-teal-700 transition hover:bg-teal-500/20 dark:text-teal-200 ${className}`}
          title={label}
          aria-label={label}
        >
          <FiHelpCircle className="h-3.5 w-3.5" />
          {pulse && <span className="guide-fab-ping absolute -top-0.5 -end-0.5 h-1.5 w-1.5 rounded-full bg-teal-400" />}
        </button>
        <SmartGuidePanel guide={guide} language={language} open={open} onClose={() => setOpen(false)} />
      </>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={openGuide}
        className={`guide-fab group fixed z-[90] flex h-9 w-9 items-center justify-center rounded-full text-slate-950 shadow-lg transition hover:scale-110 active:scale-95 ${className}`}
        style={{
          bottom: '72px',
          [isAr ? 'left' : 'right']: '16px',
          background: 'linear-gradient(135deg, #5eead4 0%, #0d9488 100%)',
          boxShadow: '0 8px 22px -6px rgba(13,148,136,0.55)',
        }}
        title={label}
        aria-label={label}
      >
        {pulse && (
          <span className="guide-fab-ping pointer-events-none absolute inset-0 rounded-full bg-teal-300/40" />
        )}
        <FiHelpCircle className="relative h-4 w-4" />
      </button>

      <SmartGuidePanel guide={guide} language={language} open={open} onClose={() => setOpen(false)} />
    </>
  )
}
